/**
 * The receiving workbench of one purchase order (batch 8 T12c2, #3033): the receipt history with the actions around it.
 *
 * Composes `ReceiptHistoryPanel`, `ReceiveGoodsDialog`, `ResolveHoldDialog` and `CloseShortDialog`. Everything is bound to the owner
 * and the purchase order: a different owner, rights or order remounts the lot and closes an open dialog. Mount it inside the purchase
 * order detail (T15); it needs only the order id and the order lines.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import { CloseShortDialog } from './CloseShortDialog';
import { ReceiptHistoryPanel } from './ReceiptHistoryPanel';
import { ReceiveGoodsDialog } from './ReceiveGoodsDialog';
import { ResolveHoldDialog, type HoldTarget } from './ResolveHoldDialog';
import { ReceivingBoundary } from './receivingParts';
import { useReceivingAccess } from './receivingSession';
import type { ReceivingLine } from './receivingTypes';

export interface PurchaseOrderReceivingPanelProps {
  purchaseOrderId: string;
  orderNumber?: string | undefined;
  lines: readonly ReceivingLine[];
}

export function PurchaseOrderReceivingPanel(props: PurchaseOrderReceivingPanelProps) {
  return (
    <ReceivingBoundary target={props.purchaseOrderId}>
      <Workbench {...props} />
    </ReceivingBoundary>
  );
}

type Open = 'none' | 'receive' | 'closeShort';

function Workbench({ purchaseOrderId, orderNumber, lines }: PurchaseOrderReceivingPanelProps) {
  const { t } = useTranslation('procurementReceiving');
  const access = useReceivingAccess();
  const [open, setOpen] = useState<Open>('none');
  const [hold, setHold] = useState<HoldTarget | null>(null);
  return (
    <Stack spacing={2}>
      {access.canReceive && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button variant="contained" onClick={() => setOpen('receive')}>
            {t('actions.receive')}
          </Button>
          <Button variant="outlined" color="warning" onClick={() => setOpen('closeShort')}>
            {t('actions.closeShort')}
          </Button>
        </Stack>
      )}
      <ReceiptHistoryPanel purchaseOrderId={purchaseOrderId} lines={lines} onDecideHold={setHold} />
      <ReceiveGoodsDialog open={open === 'receive'} onClose={() => setOpen('none')} purchaseOrderId={purchaseOrderId} orderNumber={orderNumber} lines={lines} />
      <CloseShortDialog open={open === 'closeShort'} onClose={() => setOpen('none')} purchaseOrderId={purchaseOrderId} orderNumber={orderNumber} lines={lines} />
      {hold !== null && <ResolveHoldDialog open onClose={() => setHold(null)} purchaseOrderId={purchaseOrderId} target={hold} />}
    </Stack>
  );
}
